import { cn } from '@commenda/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Pill-style segmented control for choosing between a small, fixed set of
 * mutually-exclusive views. Built on Radix Tabs so keyboard navigation, ARIA
 * roles, and focus management come for free — `Switcher` is the root
 * (analogous to `Tabs`), `SwitcherList` is the visual tray, and each
 * `SwitcherItem` is a selectable cell.
 *
 * ## When to use
 * - 2–4 short, equal-weight options (e.g. Individual / Entity, Support / Team).
 * - Switching changes the *view*, not just a filter inside a single view.
 *
 * For larger option sets or sliding-underline tabs, prefer the existing
 * `Tabs` + `HorizontalTabsList` primitives.
 *
 * ## Example
 * ```tsx
 * const [type, setType] = useState<'individual' | 'entity'>('individual');
 *
 * <Switcher value={type} onValueChange={(v) => setType(v as typeof type)}>
 *   <SwitcherList aria-label="Shareholder type">
 *     <SwitcherItem value="individual">Individual</SwitcherItem>
 *     <SwitcherItem value="entity">Entity</SwitcherItem>
 *   </SwitcherList>
 * </Switcher>
 * ```
 *
 * Items can carry icons, badges, tooltips — anything React renders:
 * ```tsx
 * <SwitcherItem value="support">
 *   <Icons.Headphones01 className="size-4" />
 *   <span>Support</span>
 *   {unread > 0 && <Badge size="sm">{unread}</Badge>}
 * </SwitcherItem>
 * ```
 */
const Switcher = TabsPrimitive.Root;
Switcher.displayName = 'Switcher';

const switcherListVariants = cva(
  'flex w-full items-center rounded-md bg-bg-secondary',
  {
    variants: {
      size: {
        sm: 'p-xs gap-xs',
        md: 'p-sm gap-sm',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

interface SwitcherListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof switcherListVariants> {}

const SwitcherList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  SwitcherListProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(switcherListVariants({ size }), className)}
    {...props}
  />
));
SwitcherList.displayName = 'SwitcherList';

const switcherItemVariants = cva(
  // flex-1 makes every item equally wide — segmented controls expect this.
  // The active state lifts onto bg-bg-primary with a hairline border + a
  // subtle shadow so the lift is unmistakable on a near-white background.
  [
    'flex-1 inline-flex items-center justify-center gap-sm whitespace-nowrap',
    'rounded-sm txt-small-medium transition-colors',
    'text-text-secondary hover:text-text-primary',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fg-brand',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'data-[state=active]:bg-bg-primary',
    'data-[state=active]:border-[0.5px] data-[state=active]:border-border-primary',
    'data-[state=active]:text-text-primary',
    'data-[state=active]:shadow-[0px_2px_4px_rgba(0,0,0,0.04)]',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'py-xs px-md',
        md: 'py-sm px-lg',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

interface SwitcherItemProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof switcherItemVariants> {}

const SwitcherItem = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  SwitcherItemProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(switcherItemVariants({ size }), className)}
    {...props}
  />
));
SwitcherItem.displayName = 'SwitcherItem';

export { Switcher, SwitcherItem, SwitcherList };
export type { SwitcherItemProps, SwitcherListProps };
